import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CustomCursor from '../../components/CustomCursor';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'problem', label: 'The Problem' },
  { id: 'vision', label: 'Product Vision' },
  { id: 'design-strategy', label: 'Design Strategy' },
  { id: 'wireframes', label: 'CGC Wireframes' },
  { id: 'seller-experience', label: 'Seller Experience' },
  { id: 'business-impact', label: 'Business Impact' },
  { id: 'results', label: 'Results & Outcomes' },
];

function SideNav() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = 'overview';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrollY) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <nav className="hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 w-[160px]">
      {sections.map((s) => (
        <button
          key={s.id}
          onClick={() => scrollTo(s.id)}
          className={`text-left text-[13px] px-3 py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
            activeSection === s.id
              ? 'text-notion-accent font-medium bg-notion-accent/10'
              : 'text-notion-secondary hover:text-notion-text hover:bg-notion-hover'
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

function PlaceholderBlock({ label }: { label: string }) {
  return (
    <div className="w-full rounded-lg border-2 border-dashed border-notion-border bg-notion-sidebar/30 flex items-center justify-center py-32">
      <span className="text-[14px] text-notion-secondary font-medium">{label}</span>
    </div>
  );
}

function FlowDiagram({ title, steps, isAfter = false }: { title: string; steps: string[]; isAfter?: boolean }) {
  return (
    <div className="bg-white border border-notion-border rounded-lg p-5 h-full">
      <h4 className={`text-[14px] font-medium mb-4 text-center ${isAfter ? 'text-emerald-600' : 'text-notion-secondary'}`}>{title}</h4>
      <div className="flex flex-col items-center gap-1.5">
        {steps.map((step, index) => (
          <div key={index} className="w-full flex flex-col items-center">
            <div className={`w-full rounded-md px-3 py-2 text-center ${
              isAfter 
                ? 'bg-emerald-50 border border-emerald-200' 
                : 'bg-notion-sidebar border border-notion-border'
            }`}>
              <p className={`text-[12px] leading-snug ${
                isAfter ? 'text-emerald-800 font-medium' : 'text-notion-secondary'
              }`}>
                {step}
              </p>
            </div>
            {index < steps.length - 1 && (
              <div className="flex items-center justify-center py-0.5">
                <i className={`ri-arrow-down-s-line text-[14px] ${
                  isAfter ? 'text-emerald-500' : 'text-notion-secondary/50'
                }`}></i>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CGCIntegrationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToPortfolio = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const portfolioSection = document.getElementById('portfolio');
      if (portfolioSection) {
        portfolioSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <SideNav />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-notion-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackToPortfolio}
              className="flex items-center gap-2 text-[14px] text-notion-secondary hover:text-notion-text transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              <span>Back to Portfolio</span>
            </button>
            <Link
              to="/"
              className="font-serif text-[18px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
            >
              annie
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-0">
        {/* Hero / Overview */}
        <section id="overview" className="pt-12 lg:pt-16 mb-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Product Design
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              UX Strategy
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              2026
            </span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium text-notion-text leading-tight tracking-tight mb-4">
            CGC Integration
          </h1>
          <p className="text-[20px] text-notion-secondary leading-relaxed mb-3">
            Enabling instant listing creation and richer buyer trust for graded comics.
          </p>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-10">
            Raremarq's entry into the premium CGC Signature Series marketplace.
          </p>

          {/* Project Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                My Role
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Founding UX & Ops Designer</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Timeline
              </h3>
              <p className="text-[15px] text-notion-text font-medium">December 2025 - January 2026</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Tools
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Figma</p>
            </div>
          </div>

          {/* Team */}
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8 mb-10">
            <h3 className="text-[12px] font-medium text-notion-secondary mb-5 uppercase tracking-wide">
              Team
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { initials: 'CT', name: 'Christopher Tung', role: 'Founder' },
                { initials: 'AD', name: 'Annie Ding', role: 'Founding UX & Ops Designer' },
                { initials: 'JS', name: 'Jacob Singer', role: 'Co-Founder and CTO' },
                { initials: 'JM', name: 'Jon', role: 'Marketing Manager' },
              ].map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-notion-accent font-medium text-sm">{m.initials}</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-notion-text">{m.name}</p>
                    <p className="text-[12px] text-notion-secondary">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/319e851a2f7fc4abae1e1dac6e18d995.png"
              alt="CGC Integration Launch Asset"
              title="CGC Integration Launch - Raremarq"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* The Problem */}
        <section id="problem" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            The Gap in Graded Comic Listings
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Raremarq was expanding into CGC-graded slabs, but the listing experience was manual,
            metadata-heavy, and visually underwhelming.
          </p>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8 mb-6">
            <ul className="space-y-3">
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-fill text-red-500"></i>
                </span>
                <span>Sellers had to input fields manually.</span>
              </li>
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-fill text-red-500"></i>
                </span>
                <span>Buyers lacked structured, trustworthy CGC data presentation.</span>
              </li>
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-fill text-red-500"></i>
                </span>
                <span>The PDP did not visually communicate premium value.</span>
              </li>
            </ul>
          </div>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            This is the metadata fields the current sellers need to fill out. We want to make that process easier.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {[
              'Certification Number',
              'Title / Series',
              'Issue Number',
              'Publisher',
              'Variant',
              'Grade',
              'Grade Category',
              'Label Type',
              'Page Quality',
              'Notes',
              'Graded Date',
            ].map((field) => (
              <div
                key={field}
                className="bg-white border border-notion-border rounded-md px-3 py-2 text-[13px] text-notion-text"
              >
                {field}
              </div>
            ))}
          </div>

          <div className="bg-notion-tag border-l-4 border-notion-accent p-6 rounded-r-lg">
            <p className="text-[16px] text-notion-text font-semibold">
              If graded slabs are premium assets, the product experience must reflect that premium
              status.
            </p>
          </div>
        </section>

        {/* Product Vision */}
        <section id="vision" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Make Graded Listings Instant and Trust-Driven
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Instead of manual input, we reimagined the entire flow:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* For Sellers */}
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[14px] font-medium text-notion-text mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-line text-notion-accent text-sm"></i>
                </span>
                For Sellers
              </h3>
              <ul className="space-y-3">
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Enter a CGC Certification ID</span>
                </li>
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Auto-import structured CGC Census data</span>
                </li>
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Generate a listing in seconds</span>
                </li>
              </ul>
            </div>

            {/* For Buyers */}
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[14px] font-medium text-notion-text mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shopping-bag-line text-notion-accent text-sm"></i>
                </span>
                For Buyers
              </h3>
              <ul className="space-y-3">
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Structured, scannable metadata</span>
                </li>
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Visual clarity around grade and label</span>
                </li>
                <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2">
                  <span className="text-notion-accent">→</span>
                  <span>Higher trust at a glance</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Flow Diagrams */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <FlowDiagram
              title="Seller Flow: Before (Manual Entry)"
              steps={[
                'Seller wants to list CGC slab',
                'Clicks "Create Listing"',
                'Manual Form Page',
                'Manually types all fields',
                'High cognitive load',
                'Possible typos',
                'Unstructured display',
                'Flat PDP presentation',
                'Lower trust clarity'
              ]}
            />

            <FlowDiagram
              title="Seller Flow: After (Automated)"
              steps={[
                'Seller wants to list CGC slab',
                'Visits raremarq.com/cgc',
                'Enters CGC Certification ID',
                'System pulls CGC data',
                'Auto-populates metadata',
                'Seller reviews listing',
                'Publishes in seconds'
              ]}
              isAfter={true}
            />
          </div>
        </section>

        {/* Design Strategy */}
        <section id="design-strategy" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Structuring Trust Through UI
          </h2>

          {/* Metadata Hierarchy */}
          <div className="mb-10">
            <h3 className="text-[20px] font-medium text-notion-text mb-4">1. Metadata Hierarchy</h3>
            <p className="text-[16px] text-notion-secondary leading-relaxed mb-4">
              I grouped and structured fields to create clear visual priority:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-notion-accent"></i>
                </span>
                <span>
                  <strong className="text-notion-text">High-impact fields</strong> (Grade, Label
                  Type, Page Quality) surfaced prominently
                </span>
              </li>
              <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-notion-accent"></i>
                </span>
                <span>
                  <strong className="text-notion-text">Supporting fields</strong> placed in
                  structured grid layout
                </span>
              </li>
              <li className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-notion-accent"></i>
                </span>
                <span>
                  <strong className="text-notion-text">Notes</strong> visually separated for
                  readability
                </span>
              </li>
            </ul>
          </div>

          {/* Visual Emphasis on Grade */}
          <div className="mb-10">
            <h3 className="text-[20px] font-medium text-notion-text mb-4">
              2. Visual Emphasis on Grade
            </h3>
            <p className="text-[16px] text-notion-secondary leading-relaxed mb-4">
              Grade became a focal visual anchor:
            </p>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-6">
              <ul className="space-y-2">
                <li className="text-[15px] text-notion-text">• Large typography</li>
                <li className="text-[15px] text-notion-text">• Clean alignment</li>
                <li className="text-[15px] text-notion-text">• High clarity</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CGC Implementation Wireframes */}
        <section id="wireframes" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            CGC Implementation Wireframes
          </h2>

          <div className="space-y-6">
            {/* Low-Fidelity Wireframes */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">Low-Fidelity Wireframes</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                Early exploration of the CGC lookup flow, focusing on the core user journey of entering a certification number and creating a listing instantly.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/fe4b58acd0977de746deb1206ae44eff.png"
                  alt="CGC Low-Fidelity Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Low-Fidelity CGC Detail Page */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">Low-Fidelity CGC Detail Page</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                Initial wireframe exploring the layout and information hierarchy for displaying CGC collectible details before creating a listing.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/703d44208d43d7a4dbc347bdb056219e.png"
                  alt="CGC Detail Page Low-Fidelity Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Low-Fidelity Example Listings */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">Low-Fidelity Example Listings</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                Early wireframe exploration for displaying CGC listings in a grid format, showing how multiple graded comics would appear in the marketplace browse experience.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/071b2582f73cf7b2caa64d77121f3a72.png"
                  alt="CGC Example Listings Low-Fidelity Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Live Auction Page */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">Live Auction Page</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                This wireframe shows how CGC details are prominently displayed on the live auction page, with structured metadata clearly visible to buyers during active bidding.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d0c130063eaa7ff409f2644e0551e841.png"
                  alt="CGC Live Auction Page Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Landing Page */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">Landing Page</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                The CGC landing page provides a streamlined entry point where sellers can instantly look up their CGC certification number and create a listing in seconds.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/31301747d3fb3f7b7d2fa5d82ec8233b.png"
                  alt="CGC Landing Page Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* CGC Detail Page */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">CGC Detail Page</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                The detail page displays all CGC collectible information in a structured, scannable format, allowing sellers to review auto-populated data before creating their listing.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/789eb3fd8b715ac18b1ddb25449ee606.png"
                  alt="CGC Detail Page Wireframe"
                  className="w-full h-auto block"
                />
              </div>
            </div>

            {/* Video Demo */}
            <div>
              <h3 className="text-[18px] font-medium text-notion-text mb-3">See It In Action</h3>
              <p className="text-[15px] text-notion-secondary leading-relaxed mb-4">
                Watch how the CGC integration works in practice, from entering a certification number to creating a complete listing.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-black aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/tEVyMez4tX4"
                  title="CGC Integration Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Seller Experience */}
        <section id="seller-experience" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Reducing Friction to Seconds
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            The seller flow was designed for speed and simplicity:
          </p>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8 mb-8">
            <ol className="space-y-4">
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-notion-accent text-white flex items-center justify-center flex-shrink-0 text-[13px] font-medium">
                  1
                </span>
                <span>Sellers visit raremarq.com/cgc</span>
              </li>
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-notion-accent text-white flex items-center justify-center flex-shrink-0 text-[13px] font-medium">
                  2
                </span>
                <span>Enter Certification ID</span>
              </li>
              <li className="text-[15px] text-notion-text leading-relaxed flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-notion-accent text-white flex items-center justify-center flex-shrink-0 text-[13px] font-medium">
                  3
                </span>
                <span>Listing auto-populates</span>
              </li>
            </ol>
          </div>

          <div className="bg-notion-tag border-l-4 border-notion-accent p-6 rounded-r-lg">
            <h4 className="text-[14px] font-medium text-notion-text mb-3 uppercase tracking-wide">
              Impact
            </h4>
            <ul className="space-y-2">
              <li className="text-[15px] text-notion-text">• Reduced input time dramatically</li>
              <li className="text-[15px] text-notion-text">• Increased graded slab supply</li>
              <li className="text-[15px] text-notion-text">
                • Encouraged collectors to test listing
              </li>
            </ul>
          </div>
        </section>

        {/* Business Impact */}
        <section id="business-impact" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            From Feature to Market Expansion
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            This feature marked Raremarq's official entry into the CGC Signature Series marketplace,
            launching with the Ronin Club Collectibles Kickoff Slab Sale.
          </p>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8 mb-8">
            <p className="text-[15px] text-notion-text leading-relaxed mb-4">
              This positioned Raremarq as a destination for:
            </p>
            <ul className="space-y-2">
              <li className="text-[15px] text-notion-text">• Witnessed signatures</li>
              <li className="text-[15px] text-notion-text">• Authenticated signatures</li>
              <li className="text-[15px] text-notion-text">• Remarqued comics</li>
              <li className="text-[15px] text-notion-text">• Graded slabs</li>
            </ul>
          </div>

          {/* Stats Block */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-notion-border rounded-lg p-6 text-center">
              <p className="text-3xl font-serif font-medium text-notion-accent mb-1">45K</p>
              <p className="text-[13px] text-notion-secondary uppercase tracking-wide">Users</p>
            </div>
            <div className="bg-white border border-notion-border rounded-lg p-6 text-center">
              <p className="text-3xl font-serif font-medium text-notion-accent mb-1">30K</p>
              <p className="text-[13px] text-notion-secondary uppercase tracking-wide">
                Monthly Pageviews
              </p>
            </div>
            <div className="bg-white border border-notion-border rounded-lg p-6 text-center">
              <p className="text-3xl font-serif font-medium text-notion-accent mb-1">2.3K</p>
              <p className="text-[13px] text-notion-secondary uppercase tracking-wide">
                Listings in a Month
              </p>
            </div>
          </div>

          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/2f10d73d651bd63197e971ab488fb6eb.png"
              alt="CGC Integration Launch Asset"
              title="CGC Integration Launch - Raremarq"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* Results & Outcomes */}
        <section id="results" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            What Success Looked Like
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Measurable goals we tracked:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              'Increased graded comic listings',
              'Increased graded comic purchases',
              'Strong seller adoption during launch event',
              'Elevated PDP quality and buyer trust',
            ].map((goal) => (
              <div
                key={goal}
                className="bg-notion-sidebar border border-notion-border rounded-lg p-5 flex items-start gap-3"
              >
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-notion-accent"></i>
                </span>
                <span className="text-[15px] text-notion-text">{goal}</span>
              </div>
            ))}
          </div>

          <div className="bg-notion-tag border-l-4 border-emerald-500 p-6 rounded-r-lg">
            <h4 className="text-[14px] font-medium text-notion-text mb-3 uppercase tracking-wide">
              Key Metric
            </h4>
            <p className="text-[16px] text-notion-text leading-relaxed">
              CGC listings grew from <strong className="text-emerald-600">~600 to 2.3K in a month</strong> after implementation, demonstrating strong seller adoption and platform growth.
            </p>
          </div>
        </section>

        {/* Reflection */}
        <section id="reflection" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">What I Learned</h2>
          <div className="bg-notion-tag border-l-4 border-notion-accent p-8 rounded-r-lg mb-8">
            <ul className="space-y-4">
              <li className="text-[16px] text-notion-text leading-relaxed">
                • Premium categories require premium UI treatment
              </li>
              <li className="text-[16px] text-notion-text leading-relaxed">
                • Metadata is not just data — it's a trust signal
              </li>
              <li className="text-[16px] text-notion-text leading-relaxed">
                • Structured information architecture directly impacts conversion
              </li>
            </ul>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-serif text-2xl font-medium text-notion-text mb-2">
                  Explore More Projects
                </h3>
                <p className="text-[15px] text-notion-secondary">
                  See how I approach design and product strategy
                </p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.raremarq.com/cgc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-notion-border text-notion-text rounded-lg text-[14px] font-medium hover:bg-notion-hover transition-colors cursor-pointer whitespace-nowrap"
                >
                  Check out Feature Live!
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </a>
                <button
                  onClick={handleBackToPortfolio}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-notion-accent text-white rounded-lg text-[14px] font-medium hover:bg-notion-accent/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Portfolio
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-notion-border bg-notion-sidebar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <Link
                to="/"
                className="font-serif text-[20px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
              >
                annie
              </Link>
              <p className="text-[13px] text-notion-secondary mt-2">
                Product Designer &amp; UX Strategist
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="mailto:ading@ucdavis.edu"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                ading@ucdavis.edu
              </a>
              <a
                href="https://www.linkedin.com/in/annieding04/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
